import { isWildCard } from '../types/card.js';
import { canPlayCard } from './validation.js';
import { getNextPlayerIndex, getNextAliveIndex, countAlivePlayers, rotateHands } from './turn.js';
import { applyAction, checkRoundEnd, startPenaltyDraw, drawCards } from './game-engine.js';
function drawCardsFromDeck(state, playerId, count) {
    const side = state.deckLeft.length >= state.deckRight.length ? 'left' : 'right';
    return drawCards(state, playerId, count, side);
}
export function hasPendingDrawObligation(state) {
    return state.pendingPenaltyDraws > 0 || state.drawStack > 0;
}
export function hasPlayableCard(hand, topCard, currentColor, canPlay = canPlayCard) {
    if (!topCard || !currentColor)
        return false;
    return hand.some(card => canPlay(card, topCard, currentColor));
}
function isLastCard(state, playerId, cardId) {
    const player = state.players.find(p => p.id === playerId);
    if (!player)
        return false;
    return player.hand.length === 1 && player.hand[0].id === cardId;
}
function isFunctionCard(card) {
    return card.type === 'draw_two' || card.type === 'wild_draw_four';
}
function getCardDrawPenalty(card) {
    if (card.type === 'draw_two')
        return 2;
    if (card.type === 'wild_draw_four')
        return 4;
    return 0;
}
function canStartDrawStack(state, card) {
    const hr = state.settings.houseRules;
    if (card.type === 'draw_two') {
        return hr.stackDrawTwo || hr.crossStack;
    }
    if (card.type === 'wild_draw_four') {
        return hr.stackDrawFour || hr.crossStack;
    }
    return false;
}
function putAttackCardOnStack(state, action, card, stackAdd) {
    if (card.type === 'wild_draw_four' && !action.chosenColor) {
        return state;
    }
    const player = state.players[state.currentPlayerIndex];
    const newHand = player.hand.filter(c => c.id !== action.cardId);
    const playedCard = card.type === 'wild_draw_four' && action.chosenColor ? { ...card, chosenColor: action.chosenColor } : card;
    const players = state.players.map((p, i) => i === state.currentPlayerIndex
        ? { ...p, hand: newHand, calledUno: newHand.length === 1 ? p.calledUno : false, unoCaught: false }
        : p);
    const nextIdx = getNextAliveIndex(players, state.currentPlayerIndex, state.direction);
    const newColor = card.type === 'draw_two' ? card.color : (action.chosenColor ?? state.currentColor);
    return checkRoundEnd({
        ...state,
        players,
        discardPile: [...state.discardPile, playedCard],
        currentColor: newColor,
        drawStack: state.drawStack + stackAdd,
        currentPlayerIndex: nextIdx,
        lastAction: action,
    }, action.playerId);
}
function applyDoubleScore(before, after) {
    if (!after.settings.houseRules.doubleScore)
        return after;
    if ((after.phase === 'round_end' || after.phase === 'game_over') &&
        before.phase === 'playing' &&
        after.winnerId !== null) {
        const winnerId = after.winnerId;
        const beforeWinner = before.players.find(p => p.id === winnerId);
        const afterWinner = after.players.find(p => p.id === winnerId);
        if (!beforeWinner || !afterWinner) {
            throw new Error(`Round winner is missing from player state: ${winnerId}`);
        }
        const beforeScore = beforeWinner.score;
        const afterScore = afterWinner.score;
        const earned = afterScore - beforeScore;
        if (earned > 0) {
            const players = after.players.map(p => (p.id === winnerId ? { ...p, score: beforeScore + earned * 2 } : p));
            return { ...after, players };
        }
    }
    return after;
}
function handleDrawUntilPlayable(state, action) {
    return applyAction(state, action);
}
function handleForcedPlayAfterDraw(stateAfterDraw, originalAction) {
    const player = stateAfterDraw.players[stateAfterDraw.currentPlayerIndex];
    if (player.hand.length === 0)
        return stateAfterDraw;
    const drawnCard = player.hand[player.hand.length - 1];
    const topCard = stateAfterDraw.discardPile[stateAfterDraw.discardPile.length - 1];
    const currentColor = stateAfterDraw.currentColor;
    if (!canPlayCard(drawnCard, topCard, currentColor)) {
        return stateAfterDraw;
    }
    const playAction = { type: 'PLAY_CARD', playerId: originalAction.playerId, cardId: drawnCard.id };
    return applyAction(stateAfterDraw, playAction);
}
export function buildRuleContext() {
    return {
        applyAction,
        checkRoundEnd,
        drawCardsFromDeck,
        startPenaltyDraw,
        putAttackCardOnStack,
        getCardDrawPenalty,
        canStartDrawStack,
        isLastCard,
        isWildCard,
        isFunctionCard,
        handleDrawUntilPlayable,
        handleForcedPlayAfterDraw,
        applyDoubleScore,
        canPlayCard,
        getNextPlayerIndex,
        getNextAliveIndex,
        countAlivePlayers,
        rotateHands,
    };
}
//# sourceMappingURL=house-rule-helpers.js.map